import { MOONPAY_WIDGET_CONTAINER_ID } from 'src/constants';
import type {
  MoonPayBalanceDepositWidgetConfig,
  MoonPayBalanceWithdrawWidgetConfig,
  MoonPayBuyConfig,
  MoonPayClaimWidgetConfig,
  MoonPayConsumerKycConfig,
  MoonPayDebug,
  MoonPayEnvironment,
  MoonPayLegacyConfig,
  MoonPayNftWidgetConfig,
  MoonPaySellConfig,
  MoonPaySwapConfig,
  MoonPaySwapsCustomerSetupConfig,
  MoonPayTopUpBalanceLedgerWidgetConfig,
  MoonPayTransactionTrackerConfig,
  MoonPayWidgetProps,
  MoonPayWithdrawLedgerWidgetConfig,
  TFlow,
} from './types';

type CommonProps<T extends TFlow> = {
  debug: MoonPayDebug;
  environment: MoonPayEnvironment;
  props: MoonPayWidgetProps<T>;
};

type ConfigConversionParams<T extends TFlow> = T extends
  | 'legacy'
  | 'moonPayBalanceDeposit'
  | 'moonPayBalanceWithdraw'
  ? Omit<CommonProps<T>, 'apiKey' | 'signature'>
  : CommonProps<T> & {
      apiKey: string;
      signature: string;
    };

export const getBuyConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'buy'>): MoonPayBuyConfig => {
  const {
    // params
    currencyCode,
    defaultCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
    // other
    overlayNode,
  } = props;

  const params: MoonPayBuyConfig['params'] = {
    apiKey,
    signature,
    currencyCode,
    defaultCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    baseOrigin,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'buy',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
    overlayNode,
  };
};

export const getSellConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'sell'>): MoonPaySellConfig => {
  const {
    // params
    quoteCurrencyCode,
    defaultCurrencyCode,
    defaultBaseCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    mpWalletId,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPaySellConfig['params'] = {
    apiKey,
    signature,
    quoteCurrencyCode,
    defaultCurrencyCode,
    defaultBaseCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    mpWalletId,
    baseOrigin,
  };

  const handlers: MoonPaySellConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'sell',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getSwapsCustomerSetupConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
}: ConfigConversionParams<
  'swapsCustomerSetup'
>): MoonPaySwapsCustomerSetupConfig => {
  const {
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    externalCustomerId,
    baseOrigin,
    // handlers
    onAuthToken,
    onLogin,
    onInitiateDeposit,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    externalCustomerId,
    baseOrigin,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onInitiateDeposit,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
  };

  return {
    flow: 'swapsCustomerSetup',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getConsumerKycConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
}: ConfigConversionParams<'consumerKyc'>): MoonPayConsumerKycConfig => {
  const {
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    baseOrigin,
    // handlers
    onLogin,
    onInitiateDeposit,
    onConsumerKycComplete,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    baseOrigin,
  };

  const handlers = {
    onLogin,
    onInitiateDeposit,
    onConsumerKycComplete,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
  };

  return {
    flow: 'consumerKyc',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getNftConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'nft'>): MoonPayNftWidgetConfig => {
  const {
    // params
    contractAddress,
    tokenId,
    listingId,
    externalTransactionId,
    baseCurrencyCode,
    redirectURL,
    walletAddress,
    subPartnerName,
    metadata,
    dynamicAssetInfo,
    sellType,
    email,
    quantity,
    baseOrigin,
    // handlers
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    contractAddress,
    tokenId,
    listingId,
    externalTransactionId,
    baseCurrencyCode,
    redirectURL,
    walletAddress,
    subPartnerName,
    metadata,
    dynamicAssetInfo,
    sellType,
    email,
    quantity,
    baseOrigin,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'nft',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getClaimConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'claim'>): MoonPayClaimWidgetConfig => {
  const {
    contractId,
    tokenId,
    initialScreen,
    // handlers
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    onTransactionCreated,
    baseOrigin,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    contractId,
    tokenId,
    initialScreen,
    baseOrigin,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'claim',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getTopUpLedgerConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'topup'>): MoonPayTopUpBalanceLedgerWidgetConfig => {
  const {
    // params
    externalCustomerId,
    theme,
    themeId,
    currencyCode,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    externalCustomerId,
    theme,
    themeId,
    currencyCode,
    baseOrigin,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'topup',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getMoonPayBalanceDepositConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<
  'moonPayBalanceDeposit'
>): MoonPayBalanceDepositWidgetConfig => {
  const {
    theme,
    themeId,
    baseOrigin,
    // handlers
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
    onTransactionCreated,
    onBalanceDepositTransferConfirmed,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    theme,
    themeId,
    baseOrigin,
  };

  const handlers = {
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
    onTransactionCreated,
    onBalanceDepositTransferConfirmed,
  };

  return {
    flow: 'moonPayBalanceDeposit',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getMoonPayBalanceWithdrawConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<
  'moonPayBalanceWithdraw'
>): MoonPayBalanceWithdrawWidgetConfig => {
  const {
    theme,
    themeId,
    baseOrigin,
    // handlers
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    theme,
    themeId,
    baseOrigin,
  };

  const handlers = {
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'moonPayBalanceWithdraw',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getWithdrawLedgerConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'withdraw'>): MoonPayWithdrawLedgerWidgetConfig => {
  const {
    // params
    externalCustomerId,
    theme,
    themeId,
    baseCurrencyCode,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    externalCustomerId,
    theme,
    themeId,
    baseCurrencyCode,
    baseOrigin,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'withdraw',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getLegacyConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<'legacy'>): MoonPayLegacyConfig => {
  const {
    // params
    url,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    url,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'legacy',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getSwapConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'swap'>): MoonPaySwapConfig => {
  const {
    // params
    walletAddress,
    walletAddressTag,
    refundWalletAddress,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyCode,
    quoteCurrencyAmount,
    colorCode,
    theme,
    themeId,
    externalCustomerId,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPaySwapConfig['params'] = {
    apiKey,
    signature,
    walletAddress,
    walletAddressTag,
    refundWalletAddress,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyCode,
    quoteCurrencyAmount,
    colorCode,
    theme,
    themeId,
    externalCustomerId,
    baseOrigin,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'swap',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getTransactionTrackerConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<
  'transactionTracker'
>): MoonPayTransactionTrackerConfig => {
  const {
    // params
    transactionId,
    colorCode,
    theme,
    themeId,
    baseOrigin,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPayTransactionTrackerConfig['params'] = {
    transactionId,
    colorCode,
    theme,
    themeId,
    baseOrigin,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onTransactionCreated,
  };

  return {
    flow: 'transactionTracker',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
    useWarnBeforeRefresh,
  };
};
